/**
 * Pure helpers for "Ask about this report" (16 Oct): the session's
 * conversation (kept in `sessionStorage`, so it survives moving around the
 * app but not closing the tab), what is sent back with a question, and where
 * a cited figure links to. Import-free, so `tests/leadership-ask.test.mjs`
 * can load it directly.
 */

export const ASK_MAX = 1000;
/** Earlier turns sent back with a question (the server takes at most 8). */
export const HISTORY_SENT = 8;
/** Longest turn text sent back (the server takes at most 4000 characters). */
export const TURN_MAX = 4000;
/** Most turns kept in the session. */
export const HISTORY_KEPT = 40;
export const STORAGE_KEY = "leadership-report-ask";

/** The example questions offered on an empty panel (keys under `leadershipReport.askReport`). */
export const EXAMPLE_KEYS = ["example1", "example2", "example3", "example4"] as const;
export type ExampleKey = (typeof EXAMPLE_KEYS)[number];

/** The sections of the page an answer can cite (ids `lr-<section>`). */
export const ASK_SECTIONS = [
  "tiles",
  "teams",
  "status",
  "sla",
  "intake",
  "getting",
  "sites",
  "people",
  "moves",
] as const;
export type AskSection = (typeof ASK_SECTIONS)[number];

export interface AskCitationLike {
  section: string;
  label: string;
  measure?: string | null;
  value?: string | null;
  range?: string | null;
  count?: number | null;
}

export interface AskTurn {
  id: string;
  role: "user" | "assistant";
  text: string;
  /** Assistant turns: what the answer is for and what it used. */
  answerable?: boolean;
  citations?: AskCitationLike[];
  unchecked?: string[];
  scope?: { range: string; first_day: string; last_day: string; filters: string };
  pulledAt?: string;
}

/** Whether a question may be sent (not blank, not too long). */
export function canAsk(question: string): boolean {
  const trimmed = question.trim();
  return trimmed.length > 0 && trimmed.length <= ASK_MAX;
}

/** The page anchor of a section, or null for an unknown one. */
export function sectionAnchor(section: string): string | null {
  return (ASK_SECTIONS as readonly string[]).includes(section) ? `lr-${section}` : null;
}

/**
 * Whether a citation can open the drill-in drawer for the page as it is: it
 * names a measure and is for the page's range (a figure for another range
 * would open other tickets than it counts).
 */
export function canDrill(citation: AskCitationLike, pageRange: string): boolean {
  if (!citation.measure) {
    return false;
  }
  return !citation.range || citation.range === pageRange;
}

/** The earlier turns to send with the next question: the newest few, text only, capped. */
export function historyForApi(
  turns: readonly AskTurn[],
): { role: "user" | "assistant"; text: string }[] {
  return turns
    .filter((turn) => turn.text.trim().length > 0)
    .slice(-HISTORY_SENT)
    .map((turn) => ({ role: turn.role, text: turn.text.slice(0, TURN_MAX) }));
}

function isTurn(value: unknown): value is AskTurn {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === "string" &&
    (v.role === "user" || v.role === "assistant") &&
    typeof v.text === "string"
  );
}

/** The session's conversation from storage; anything malformed is dropped. */
export function parseHistory(raw: string | null): AskTurn[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isTurn).slice(-HISTORY_KEPT) : [];
  } catch {
    return [];
  }
}

/** The conversation as stored (the newest `HISTORY_KEPT` turns). */
export function serializeHistory(turns: readonly AskTurn[]): string {
  return JSON.stringify(turns.slice(-HISTORY_KEPT));
}

let counter = 0;
/** A turn id unique within the session. */
export function turnId(now: number = Date.now()): string {
  counter += 1;
  return `${now.toString(36)}-${counter}`;
}
